import type Locator from '../Locator';

/**
 * This unexported unique symbol powers non-exhaustiveness in the type system,
 * because it can't be named outside this module.
 */
const NON_EXHAUSTIVE = Symbol('non_exhaustive');

/** The nonexhaustive base type, for use with typed enums (instead of discriminated unions). */
type EnumNonExhaustive = typeof NON_EXHAUSTIVE;

/**
 * This type is not concretely used;
 * instead it exists to encourage callers to use a default case,
 * which allows this module to freely add additional variants later.
 *
 * By setting `kind` to the type of a symbol which is not exported,
 * the name of this variant is unexpressable to callers,
 * requiring them to use a `default` case to handle it.
 */
type NonExhaustive = {
  kind: EnumNonExhaustive,
};

/** Defines an array which cannot be empty at construction. */
export type NonEmptyArray<T> = [T, ...T[]];

/** Type guard for an array to test whether it is non-empty. */
export const isNonEmptyArray = <T>(a: T[]): a is NonEmptyArray<T> => a.length > 0;

/**
 * Enumerates a single Java symbol.
 *
 * This type makes up a "fully qualified symbol name".
 * What goes into a FQSN is going to change a little
 * based on the implementation of each programming language.
 *
 * The intention is that the FQSN disambiguates cases like:
 * - A *method* and a *standalone function*, both named `foo`.
 * - Two standalone functions, both named `foo`, in different modules.
 * - Two methods, both named `foo`, that exist on different classes.
 * - A *class* method and an *instance* method, both named `foo`.
 *
 * For example, for the following symbol:
 * ```
 * com.thoughtworks.xstream::XStream::fromXML
 * ```
 *
 * This type is expressed as:
 * ```
 * [ { kind: 'package',         label: 'com.thoughtworks.xstream' }
 * , { kind: 'class',           label: 'XStream' }
 * , { kind: 'instance_method', label: 'fromXML' }
 * ]
 * ```
 *
 * Different languages modeled by this package
 * will each have their own symbol type.
 *
 * Future extensions to this type may do things like record the metadata
 * (e.g. file path, line numbers) to better display this information
 * to users in the FOSSA UI; however the initial iteration
 * does not do so to limit the MVP surface area.
 */
export type SymbolJava = NonEmptyArray<NonExhaustive | {
  /** The discriminator for each variant. */
  kind
    : 'package'
    | 'class'
    | 'instance_method'
    | 'class_method'
    | 'constructor'
    // represents segment of a symbol e.g. org.fossa.com may have 'org' as raw_symbol_segment
    | 'raw_symbol_segment'

  /**
   * The actual name of the element in the code.
   * This is an associated property for all variants.
   */
  label: string,
}>;

/**
 * Enumerates a call path for Java.
 * Call paths are made up of symbols.
 *
 * As written this currently appears to be an alias;
 * the idea is that this will be expanded later:
 * ```
 * type Symbol = SymbolJava | SymbolPython | SymbolJavaScript;
 * ```
 *
 * The `kind` property is used to discriminate variants.
 */
export type CallPathJava = {
  /** The discriminator for this variant. */
  kind: 'java',

  /** The symbols that make up the call path. */
  symbols: NonEmptyArray<SymbolJava>
}

/**
 * A generic call path.
 *
 * A "call path" is a sequence of symbols that lead to a terminating symbol;
 * the terminating symbol is the function that is called,
 * and intermediate symbols are the functions that end up calling it.
 *
 * As written this currently appears to be an alias;
 * the idea is that this will be expanded later:
 * ```
 * type CallPath = CallPathJava | CallPathPython | CallPathJavaScript;
 * ```
 *
 * The `kind` property is used to discriminate variants.
 */
export type CallPath
  = NonExhaustive
  | CallPathJava;

/**
 * Reports where a given call path originates in first party code
 * without disclosing the actual symbol names in the first party code.
 *
 * The "origin of a call path" means "where, in the first party code, the chain begins
 * that eventually ends in calling this vulnerable function".
 *
 * The intention is to provide the user with this information so that they
 * can go look at their first party code. Theoretically it'd be possible for
 * FOSSA to provide a direct link to this part of the file (e.g. if hosted on GitHub)
 * or to provide a snippet (e.g. if quick importing) using this data.
 *
 * Of course, this is not the _full_ call path inside the first party code
 * (which would include e.g. the user's main function, and any intermediate functions).
 * Instead this is the place in the first party code where the first part of the
 * 3rd party call graph is called.
 * The assumption is that the user can then work out the call graph
 * of their first party code themselves, and storing that on our end
 * would not be useful (and would be confusing to represent,
 * since we can't show them their actual symbol names, only the file locations).
 */
export type CallRoot = {
  /** The path of the files */
  filePath: string[],

  /** The line on which the call begins. */
  lineStart?: number,

  /** The line on which the call ends. */
  lineEnd?: number,
}

/**
 * Describes a call path that has been joined to first party code with a call root.
 *
 * One can envision a `CallPath` as a sort of "relative file path",
 * while the `CallRoot` is a sort of file system mount point.
 * Similar to how a fully qualified file path is created by joining the two,
 * a fully qualified call path is created by joining these two.
 *
 * The "root" and "relative" naming of these properties lean into this idea.
 */
export type AbsoluteCallPath = {
  /** The place where the relative part of the call path began in first party code.  */
  root: CallRoot,

  /** The rest of the call path through the dependencies of first party code. */
  relative: CallPath,
}

/** The concrete value of the `ReachableConfirmed` kind. */
export const EnumReachableConfirmed = 'reachable_confirmed';

/** The concrete value of the `ReachableNone` kind. */
export const EnumReachableNone = 'reachable_none';

/** The concrete value of the `ReachableAssumed` kind. */
export const EnumReachableAssumed = 'reachable_assumed';

/**
 * `Reachable` states as an enum of raw values.
 *
 * Similar to the discriminated unions exported in this module,
 * this enum is marked non-exhaustive as well and should be handled
 * similarly to the discriminated unions.
 */
// Important: A `Reachable` discriminant must exist per variant.
// Important: The `ReachabilityExport` table must be updated if this changes.
export type EnumReachable
  = EnumNonExhaustive
  | typeof EnumReachableAssumed
  | typeof EnumReachableConfirmed
  | typeof EnumReachableNone;

/** States that the associated CVE was not reachable. */
export type ReachableNone = {
  kind: typeof EnumReachableNone
}

/**
 * States that the associated CVE was assumed to be reachable.
 *
 * CVEs that are assumed reachable are not necessarily reachable;
 * instead this is the default response when the reachability
 * data for the CVE is not available.
 */
export type ReachableAssumed = {
  kind: typeof EnumReachableAssumed
}

/**
 * States that the associated CVE was concretely reachable.
 *
 * The provided call paths concretely lead to at least one
 * vulnerable function in the dependency, as reported by the CVE.
 * Which vulnerable function is reached is expressed by the last element in the path.
 *
 * A given CVE may have multiple call paths to the same function,
 * or multiple call paths to different vulnerable functions.
 * Only vulnerable functions are expressed in the provided call paths.
 *
 * The associated call paths are versioned because different versions of the
 * call path extraction algorithm may provide different data.
 */
export type ReachableConfirmed = {
  kind: typeof EnumReachableConfirmed,
  callPaths: NonEmptyArray<AbsoluteCallPath>,
}

/**
 * The reachability status of the associated CVE.
 *
 * The `kind` property is used to discriminate variants.
 */
// Important: an `EnumReachable` variant must exist for each discriminant.
export type Reachable
  = NonExhaustive
  | ReachableNone
  | ReachableAssumed
  | ReachableConfirmed;

/**
 * The reachability data for a given revision per reported CVE.
 *
 * Each given CVE may be reachable at multiple call paths.
 * A CVE that is reported as an issue but is not reachable
 * may or may not be included in this object.
 * If it is included, it should be an empty array.
 */
export type Reachability = Record<string, Reachable>;

/** Provide a `CallPath` for demonstrating the non-exhaustiveness of the discriminated union. */
export const testNonExhaustiveCallPath = (): CallPath => ({ kind: NON_EXHAUSTIVE });

/**
 * The known vulnerable function. It represents
 * fully qualified symbol path to the function
 * which is vulnerable. It uses representative identifier
 * as label for symbol. For example, in JAVA language,
 * one can have multiple class methods with same name,
 * as long as the method args differ, etc. For this reason,
 * we use representative label.
 *
 * For example,
 * ```java
 *
 * class A {
 *   int counter;
 *
 *   void f() {
 *       class D {
 *          int counter;
 *       }
 *
 *       D d = new D();
 *       Thread e = new Thread() {
 *           public void run() {
 *               System.out.print("Hello from " + getName());
 *           }
 *       };
 *
 *       System.out.println(d.counter);
 *       e.start();
 *   }
 *
 *   void f(String name) {
 *     System.out.print("Hello " + name);
 *   }
 * }
 *
 * ```
 *
 * In above example, vulnerable function `f(String name)` would be,
 * represented by qualified name as follows.
 *
 * {
 *   kind: 'java',
 *   symbol: [
 *       { kind: 'class',         label: 'C'         },
 *       { kind: 'class_method',  label: 'f(String)' },
 *   ]
 * }
 *
 */
export type VulnerableFunction
  = NonExhaustive // Encourage the caller to deal with unrecognized shape!
  | { kind: 'java', symbol: SymbolJava };

export type VulnerableComponent =
  {
    cve: string,
    dependencyRevisionId: Locator,
    function: VulnerableFunction,
  }
